import React from "react";
import { Link } from "react-router-dom";
import { Activity, ChevronRight, Filter, Layers, TrendingUp, Zap } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function Landing() {
  return (
    <div id="main-content" className="min-h-screen" data-testid="landing-page">
      <Header />

      {/* Ticker tape */}
      <div className="border-b border-white/5 overflow-hidden">
        <div className="ticker-tape flex gap-10 py-2.5 whitespace-nowrap label-mono">
          {Array(4).fill([
            "ES +0.42%", "NQ +0.83%", "BTC -1.21%", "AAPL +1.15%", "NVDA +2.45%",
            "SPY +0.31%", "GC -0.18%", "CL +1.92%", "TSLA -2.41%", "QQQ +0.62%",
          ]).flat().map((t, i) => (
            <span key={i} className={t.includes("-") ? "text-[var(--bear)]" : "text-[var(--bull)]"}>{t}</span>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className="relative px-4 sm:px-6 py-20 lg:py-28 overflow-hidden">
        <div className="ambient-orb" />
        <div className="relative max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-md mb-6" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--bull)] pulse-dot"></span>
              <span className="text-xs text-white/80 tracking-tight">Live scanner · 60+ tickers</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-[-0.03em] leading-[1.05] text-white">
              Hunt the cleanest
              <br />
              <span className="aurora-text">setups before</span>
              <br />
              <span className="text-white/80">everyone else.</span>
            </h1>
            <p className="mt-6 text-white/55 text-base sm:text-lg max-w-xl leading-relaxed">
              A premium scanner for stocks, ETFs, crypto proxies, futures and micros.
              Build custom scans with RSI, MACD, VWAP, Bollinger, RVOL and EMA — ranked 1–100, refreshed live.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" className="btn-primary flex items-center gap-2" data-testid="hero-cta-primary">
                Start scanning free <ChevronRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" className="btn-ghost" data-testid="hero-cta-secondary">View pricing</Link>
            </div>
            <div className="mt-6 label-mono">No credit card required · Cancel anytime</div>
          </div>

          <div className="relative">
            <div className="absolute -inset-8 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 50%, rgba(108,92,231,0.18), transparent 60%)", filter: "blur(40px)" }} />
            <div className="relative card-surface p-5 float">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[var(--bull)] pulse-dot"></span>
                  <span className="label-mono">Live scan · 15m</span>
                </div>
                <span className="signal-pill neutral">12 matches</span>
              </div>
              <div className="space-y-1.5">
                {[
                  { t: "NVDA", d: "BULL", s: 87, c: "+2.45", rsi: 31.2 },
                  { t: "TSLA", d: "BEAR", s: 76, c: "-2.41", rsi: 71.8 },
                  { t: "ES", d: "BULL", s: 72, c: "+0.42", rsi: 38.4 },
                  { t: "AAPL", d: "BULL", s: 68, c: "+1.15", rsi: 42.1 },
                  { t: "MES", d: "BULL", s: 65, c: "+0.39", rsi: 41.7 },
                  { t: "BTC", d: "BEAR", s: 61, c: "-1.21", rsi: 67.3 },
                ].map((r) => (
                  <div key={r.t} className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-white/5 bg-white/[0.02] hover:border-white/15 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-white text-[13px] font-semibold tracking-tight w-12">{r.t}</span>
                      <span className={`signal-pill ${r.d === "BULL" ? "bull" : "bear"}`}>{r.d}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-white/45 text-[11px]">RSI {r.rsi}</span>
                      <span className={`font-mono text-[11px] ${r.c.startsWith("-") ? "text-bear" : "text-bull"}`}>{r.c}%</span>
                      <span className="font-mono text-white text-lg font-semibold w-7 text-right tabular-nums">{r.s}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature blocks */}
      <section className="px-4 sm:px-6 py-20 border-t border-white/5">
        <div className="max-w-[1200px] mx-auto">
          <div className="label-mono mb-3">Why FlowFinder</div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-white max-w-2xl">
            Built for traders who actually trade.
          </h2>
          <div className="grid md:grid-cols-3 gap-4 mt-10">
            {[
              { icon: Filter, t: "Filter builder", d: "Compose scans with markets, asset types, timeframes, indicators and signal strength. Every combination, your way." },
              { icon: Activity, t: "Real indicators", d: "14-RSI, 12/26/9 MACD, 20-period Bollinger, session VWAP, RVOL, EMA 9/20/50 — computed live from a streaming Finnhub WebSocket feed." },
              { icon: Layers, t: "All markets", d: "Stocks · ETFs · Crypto proxies · Futures · Micros — clean momentum scans for whatever you trade." },
              { icon: TrendingUp, t: "Setup score 1–100", d: "Each ticker is scored on bullish/bearish strength so you instantly know what's worth watching." },
              { icon: Zap, t: "Heatmap & watchlist", d: "Momentum heatmap of strongest movers, plus a personal watchlist with TradingView-style charts." },
              { icon: Activity, t: "Live Board", d: "Curated leaderboard of strongest setups, biggest volume spikes, hottest ETFs and top momentum stocks — updated every minute." },
            ].map((f, i) => (
              <div key={i} className="card-surface p-6 glass-hover" data-testid={`feature-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center mb-4">
                  <f.icon className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-white font-semibold tracking-tight text-[15px]">{f.t}</h3>
                <p className="text-white/55 text-sm mt-2 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 py-20 border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(800px 400px at 50% 100%, rgba(108,92,231,0.15), transparent 60%)" }} />
        <div className="relative max-w-[900px] mx-auto text-center">
          <div className="label-mono mb-3">Get started</div>
          <h2 className="text-3xl sm:text-5xl tracking-tight font-semibold text-white">Ready to scan smarter?</h2>
          <p className="text-white/55 mt-4 max-w-2xl mx-auto leading-relaxed">
            Free tier includes RSI &amp; MACD filters and a 5-ticker watchlist. Upgrade for live OHLCV data,
            futures, VWAP / Bollinger filters and premium scanner speed.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/register" className="btn-primary" data-testid="bottom-cta">Create free account</Link>
            <Link to="/pricing" className="btn-ghost" data-testid="bottom-cta-pricing">Compare plans</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
